import { useState } from 'react'
import type { JSX } from 'react'
import { useTranslation } from 'react-i18next'
import { formatDateVN, formatVND, toMoney } from '@rs/contracts'
import { AlertModal, Button, Card } from '@rs/ui'
import { qk, useList, messageOf } from '../../shared/queries.ts'
import { useAuth } from '../../app/auth.tsx'
import { fetchMyBids, submitOfferApi, type MyBid } from '../bidding/api.ts'

/**
 * ══ CỔNG THẦU PHỤ ▸ 📣 CHÀO GIÁ — chép `NccPortal.tsx` tab `bidding` (`:202-233`, `:842-920`) ═══════════════════════════════════
 *
 * ⭐ Đơn điều hành đang MỞ THẦU (chưa bắn chặng, còn hạn tới trước ngày chạy 1 ngày). Thầu phụ khai TÊN CÔNG TY · MST (bắt buộc —
 *   điều hành chọn giá là tạo NCC từ đây) · SĐT · loại dịch vụ · giá · có hóa đơn + %VAT · ghi chú. Tài khoản dùng chung chào được cho
 *   nhiều công ty; cùng công ty chào lại thì ĐÈ giá cũ.
 * ⚠ MÀN NÀY KHÔNG BAO GIỜ BÀY GIÁ CỦA NHÀ THẦU KHÁC — máy chủ không gửi. Màn KHÔNG gửi `vendorId`: mình là ai do PHIÊN quyết.
 */

interface Draft { company: string; taxCode: string; phone: string; price: string; hasInvoice: boolean; vatPercent: string; serviceMode: 'rail' | 'road' | 'sea'; note: string }

export const BiddingTab = (): JSX.Element => {
  const { t } = useTranslation()
  const { me } = useAuth()
  const [open, setOpen] = useState<Set<string>>(new Set())
  const [drafts, setDrafts] = useState<Record<string, Draft>>({})
  const [notice, setNotice] = useState<string | null>(null)
  const { rows, err: loadErr, reload } = useList(qk.myBids(), fetchMyBids)
  const list = rows ?? []

  // Mặc định lấy công ty · MST của lần chào gần nhất (tài khoản dùng chung thì sửa lại).
  const last = list.flatMap((b) => b.myOffers).sort((a, b) => b.submittedAt.localeCompare(a.submittedAt))[0]
  const draftOf = (id: string): Draft => drafts[id] ?? {
    company: last?.companyName ?? me?.name ?? '', taxCode: last?.taxCode ?? '', phone: last?.phone ?? '',
    price: '', hasInvoice: false, vatPercent: '8', serviceMode: 'road', note: '',
  }
  const setDraft = (id: string, patch: Partial<Draft>): void => { setDrafts((d) => ({ ...d, [id]: { ...draftOf(id), ...patch } })) }
  const toggle = (id: string): void => { setOpen((s) => { const n = new Set(s); if (n.has(id)) n.delete(id); else n.add(id); return n }) }

  const submit = (b: MyBid): void => {
    const d = draftOf(b.request.id)
    if (d.company.trim() === '') { setNotice(t('ncc.bidding.needCompany')); return }
    if (!/^\d{10,13}$/.test(d.taxCode.trim())) { setNotice(t('ncc.bidding.needTaxCode')); return }
    const price = Number(d.price)
    if (!(price > 0)) { setNotice(t('ncc.bidding.needPrice')); return }
    void submitOfferApi({
      requestId: b.request.id, amount: toMoney(price), companyName: d.company.trim(), taxCode: d.taxCode.trim(),
      serviceMode: d.serviceMode, hasInvoice: d.hasInvoice,
      ...(d.phone.trim() === '' ? {} : { phone: d.phone.trim() }),
      ...(d.hasInvoice ? { vatPercent: Number(d.vatPercent) || 0 } : {}),
      ...(d.note.trim() === '' ? {} : { note: d.note.trim() }),
    }).then(() => {
      setOpen((s) => { const n = new Set(s); n.delete(b.request.id); return n })
      setDrafts((x) => { const n = { ...x }; delete n[b.request.id]; return n })
      reload()
      setNotice(t('ncc.bidding.sentAlert'))
    }, (e: unknown) => { setNotice(messageOf(e)) })
  }

  const inCls = 'border border-slate-300 rounded-lg px-2 py-1.5 w-full'
  const lbl = 'text-[10px] font-semibold text-slate-500 mb-0.5'
  return (
    <div className="space-y-2.5">
      <div className="text-[11px] text-slate-400 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">{t('ncc.bidding.hint')}</div>
      {loadErr !== null && <div role="alert" className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">{loadErr}</div>}
      {list.length === 0 ? <Card className="p-6 text-center text-slate-400 text-sm">{t('ncc.bidding.empty')}</Card> : list.map((b) => {
        const isOpen = open.has(b.request.id)
        const d = draftOf(b.request.id)
        return (
          <Card key={b.request.id} className="!p-0 overflow-hidden">
            <div className="px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center gap-2 flex-wrap">
              <span className="font-mono text-[12px] font-bold text-brand-600">{b.job.ref}</span>
              <span className="text-[11px] text-slate-500">{b.job.pickupAddr ?? '—'} → {b.job.dropAddr ?? '—'}</span>
              <span className="text-[11px] text-slate-400 ml-auto">{b.job.planDate === null ? '—' : formatDateVN(b.job.planDate)}</span>
              <button type="button" onClick={() => { toggle(b.request.id) }}
                      className="text-[11px] font-bold text-white bg-brand-500 hover:bg-brand-600 rounded-lg px-2.5 py-1">{isOpen ? t('common.close') : t('ncc.bidding.offerBtn')}</button>
            </div>
            {b.myOffers.length > 0 && (
              <div className="px-3 py-2 border-b border-slate-100 space-y-1">
                {b.myOffers.map((o) => (
                  <div key={o.id} className="flex items-center gap-2 text-[11px] flex-wrap">
                    <span className="font-semibold text-slate-600">{o.companyName ?? o.vendorName}</span>
                    <span className="tabular-nums font-bold text-slate-700">{formatVND(toMoney(o.amount))}</span>
                    <span className="text-slate-400">{o.hasInvoice ? t('ncc.bidding.hasInvoiceShort', { pct: o.vatPercent ?? 0 }) : t('ncc.bidding.noInvoiceShort')}</span>
                    {o.status === 'won' && <span className="text-[10px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">{t('ncc.bidding.selectedBadge')}</span>}
                  </div>
                ))}
              </div>
            )}
            {isOpen && (
              <div className="p-3 grid grid-cols-1 sm:grid-cols-2 gap-2 text-[12px]">
                <label><div className={lbl}>{t('ncc.bidding.fCompany')}</div>
                  <input value={d.company} onChange={(e) => { setDraft(b.request.id, { company: e.target.value }) }} className={inCls} /></label>
                <label><div className={lbl}>{t('ncc.bidding.fTaxCode')}</div>
                  <input value={d.taxCode} inputMode="numeric" placeholder={t('ncc.bidding.fTaxCodePh')} onChange={(e) => { setDraft(b.request.id, { taxCode: e.target.value.replace(/[^\d]/g, '') }) }} className={inCls} /></label>
                <label><div className={lbl}>{t('ncc.bidding.fPhone')}</div>
                  <input value={d.phone} onChange={(e) => { setDraft(b.request.id, { phone: e.target.value }) }} className={inCls} /></label>
                <label><div className={lbl}>{t('ncc.bidding.fService')}</div>
                  <select value={d.serviceMode} onChange={(e) => { setDraft(b.request.id, { serviceMode: e.target.value as Draft['serviceMode'] }) }} className={inCls}>
                    <option value="rail">🚆 {t('ncc.bidding.svcRail')}</option>
                    <option value="road">🚚 {t('ncc.bidding.svcRoad')}</option>
                    <option value="sea">🚢 {t('ncc.bidding.svcSea')}</option>
                  </select></label>
                <label><div className={lbl}>{t('ncc.bidding.fPrice')}</div>
                  <input value={d.price} inputMode="numeric" onChange={(e) => { setDraft(b.request.id, { price: e.target.value.replace(/[^\d]/g, '') }) }} className={`${inCls} font-bold`} /></label>
                <label className="flex items-center gap-1.5 sm:mt-4">
                  <input type="checkbox" checked={d.hasInvoice} onChange={(e) => { setDraft(b.request.id, { hasInvoice: e.target.checked }) }} />
                  <span className="text-[11px] font-semibold text-slate-600">{t('ncc.bidding.fHasInvoice')}</span>
                </label>
                {d.hasInvoice && (
                  <label><div className={lbl}>{t('ncc.bidding.fVatPct')}</div>
                    <input value={d.vatPercent} inputMode="numeric" onChange={(e) => { setDraft(b.request.id, { vatPercent: e.target.value.replace(/[^\d]/g, '') }) }} className={inCls} /></label>
                )}
                <label className="sm:col-span-2"><div className={lbl}>{t('ncc.bidding.fNote')}</div>
                  <input value={d.note} onChange={(e) => { setDraft(b.request.id, { note: e.target.value }) }} className={inCls} /></label>
                <div className="sm:col-span-2 flex justify-end">
                  <Button size="sm" onClick={() => { submit(b) }}>{t('ncc.bidding.submitBtn')}</Button>
                </div>
              </div>
            )}
          </Card>
        )
      })}
      {notice !== null && <AlertModal message={notice} onClose={() => { setNotice(null) }} />}
    </div>
  )
}
